import h from 'hyperscript';
import { Overlay } from 'ol';
import markerIcon from '@disy/cadenza-icons/map-marker.svg';
import copyIcon from '@disy/cadenza-icons/copy-to-clipboard-small.svg';

import { createIconButton } from 'ui/button/button';

import type { Map } from 'cadenza/map/map';
import { SketchDrawInteraction } from 'cadenza/workbook/sketch/interactions/sketch-draw-interaction';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { removeFeatureOverlay } from 'cadenza/workbook/sketch/sketch-layer/sketch-style';
import { icon } from 'cadenza/utils/icon/icon';
import i18n from 'cadenza/map/interactions/capture-coordinates-interaction.properties';
import { copyToClipboard } from 'cadenza/utils/clipboard';
import { formatCoordinate, formatCoordinateForCopy } from 'cadenza/utils/format/format';
import { SketchFeatureProperty } from 'cadenza/workbook/sketch/sketch-layer/sketch-types';
import { assert } from 'cadenza/utils/custom-error';
import { uniqueId } from 'cadenza/utils/unique-id';

import type { StyleLike } from 'ol/style/Style';
import type { Geometry, Point } from 'ol/geom';
import type VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';

import './capture-coordinates-interaction.css';

export class CaptureCoordinatesInteraction extends SketchDrawInteraction {

  _map;
  _overlay!: Overlay; // added in

  constructor (map: Map, source: VectorSource<Geometry>, featureStyle: StyleLike) {
    super({ map, source, type: GeometryType.POINT, featureStyle });
    this._map = map;
  }

  onDrawStart (feature: Feature) {
    super.onDrawStart(feature);
    setupCoordinatesOverlay(feature, this._map);
  }

  _reset () {
    this._map.removeOverlay(this._overlay);
  }

  onActivation () {
    super.onActivation();
    this._map.getTargetElement().classList.add('is-cursor-pointer');
  }

  onDeactivation () {
    super.onDeactivation();
    this._reset();
    this._map.getTargetElement().classList.remove('is-cursor-pointer');
  }

}

export function setupCoordinatesOverlay (feature: Feature, map: Map) {
  removeFeatureOverlay(feature, map);
  const overlay = createCoordinatesOverlay(feature as Feature<Point>);
  map.addOverlay(overlay);
  feature.set(SketchFeatureProperty.OVERLAY_ID, overlay.getId());
}

function createCoordinatesOverlay (feature: Feature<Point>) {
  const geometry = feature.getGeometry();
  const coordinates = geometry?.getCoordinates() as number[];
  assert(coordinates && geometry, 'Geometry and coordinates are required !');
  const formattedCoordinate = formatCoordinate(coordinates);
  const copyCoordinatesButton = createIconButton(
    icon(copyIcon),
    i18n('copyToClipboard'),
    {
      variant: 'primary',
      size: 's',
      onclick: () => copyToClipboard(formatCoordinateForCopy(coordinates)),
      styleClass: 'coordinates-tooltip--copy-button'
    });
  return new Overlay({
    id: uniqueId(),
    element: h(
      '.tooltip.coordinates-tooltip',
      h('.d-stack-h.space-1', [
        icon(markerIcon, { size: 'xs', styleClass: 'coordinates-tooltip--marker-icon' }),
        formattedCoordinate,
        copyCoordinatesButton
      ])
    ) as HTMLElement,
    position: geometry.getLastCoordinate(),
    positioning: 'bottom-center',
    offset: [ 0, -12 ] // the height of the arrow below the tooltip is 12px
  });
}
